import { HttpClient } from '@angular/common/http';
import { Injectable, Signal, inject, signal } from '@angular/core';
import { DateTime } from 'luxon';
import { firstValueFrom, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Banner, BannerType } from '../models';
import { KioskService } from './kiosk/kiosk.service';

@Injectable({ providedIn: 'root' })
export class BannerService {
  private http = inject(HttpClient);
  private kioskService = inject(KioskService);

  private readonly bannerEndpoint = `${environment.apiOrigin}/banner`;
  private readonly CACHE_SIZE = 1;

  private cache: Observable<Banner[]>;

  getBanners(merchantId: number) {
    return this.getActiveBanners(merchantId).pipe(
      map((banners) => {
        const today = DateTime.now();
        return banners.filter(
          (b) =>
            b.type !== BannerType.Hero &&
            b.type !== BannerType.Streamer &&
            DateTime.fromISO(b.startDate).startOf('day') <= today.startOf('day') &&
            (!b.endDate || DateTime.fromISO(b.endDate).startOf('day') >= today.startOf('day')),
        );
      }),
    );
  }

  getStreamerBanners(merchantId: number) {
    return this.getActiveBanners(merchantId).pipe(
      map((banners) => {
        if (!banners?.length) return [];

        const today = DateTime.now();
        banners = banners.filter(
          (b) =>
            b.type === BannerType.Streamer &&
            DateTime.fromISO(b.startDate).startOf('day') <= today.startOf('day') &&
            (!b.endDate || DateTime.fromISO(b.endDate).startOf('day') >= today.startOf('day')),
        );

        return banners;
      }),
    );
  }

  getHeroBanners(merchantId: number, productId?: number) {
    return this.getActiveBanners(merchantId).pipe(
      map((banners) => {
        if (!banners?.length) return [];

        const today = DateTime.now();
        banners = banners.filter(
          (b) =>
            b.type === BannerType.Hero &&
            DateTime.fromISO(b.startDate).startOf('day') <= today.startOf('day') &&
            (!b.endDate || DateTime.fromISO(b.endDate).startOf('day') >= today.startOf('day')) &&
            (!b.productOnly || !!productId) &&
            (!b.kioskOnly || this.kioskService.isKiosk) &&
            (!productId || (b.productIds && b.productIds.findIndex((id) => id === productId) !== -1)),
        );

        banners.forEach((banner) => {
          banner.title = banner.title.replace(/\\n/g, '\n');
          return banner;
        });

        return banners;
      }),
    );
  }

  private getActiveBanners(merchantId: number) {
    if (!this.cache) {
      this.cache = this.requestActiveBanners(merchantId).pipe(shareReplay(this.CACHE_SIZE));
    }

    return this.cache;
  }

  private requestActiveBanners(merchantId: number) {
    return this.http.get<Banner[]>(`${this.bannerEndpoint}/${merchantId}/active`);
  }
}
